import React from 'react'
import { gql, useMutation } from '@apollo/client'
import { DateTime } from 'luxon'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import BlockIcon from 'components/material-symbol/icons/Block'
import DeleteOutlinedIcon from 'components/material-symbol/icons/Delete'
import { makeStyles } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'
import { CALLOUT_FOREGROUND } from 'lib/form-field-styles'

const useStyles = makeStyles(theme => ({
  bodyText: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  detailsCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#ECEFF1',
    borderRadius: 12,
  },
  detailsIcon: {
    color: '#4F5B62',
  },
  detailsTextCol: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  detailsDate: {
    fontWeight: 600,
    lineHeight: 1.3,
    color: CALLOUT_FOREGROUND,
  },
  detailsTime: {
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.3,
  },
  confirmText: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: CALLOUT_FOREGROUND,
    fontWeight: 500,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(3),
  },
  cancelAction: {
    textTransform: 'none',
    fontWeight: 600,
    color: CALLOUT_FOREGROUND,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  deleteAction: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 24,
    padding: theme.spacing(1, 2.5),
    minWidth: 'auto',
    boxShadow: 'none',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.75),
      marginLeft: 0,
    },
  },
}))

const isIsoString = val =>
  typeof val === 'string' && /\d{4}-\d{2}-\d{2}T/.test(val)

const formatTime12 = iso => {
  const dt = DateTime.fromISO(iso)
  return dt.minute === 0 ? dt.toFormat('h a') : dt.toFormat('h:mm a')
}

const formatBlockForDisplay = blockTime => {
  if (isIsoString(blockTime.startTime)) {
    const start = DateTime.fromISO(blockTime.startTime)
    const end = DateTime.fromISO(blockTime.endTime)
    const sameDay = start.hasSame(end, 'day')
    const date = sameDay
      ? start.toFormat('EEE, MMM d, yyyy')
      : `${start.toFormat('EEE, MMM d')} – ${end.toFormat('EEE, MMM d, yyyy')}`
    const time = blockTime.allDay
      ? 'All day'
      : `${formatTime12(blockTime.startTime)} – ${formatTime12(
        blockTime.endTime,
      )}`
    return { date, time }
  }

  const sameDay = blockTime.startDate === blockTime.endDate
  const date = sameDay
    ? blockTime.startDate
    : `${blockTime.startDate} – ${blockTime.endDate}`
  const time =
    blockTime.startTime === 'All day' || blockTime.endTime === 'All day'
      ? 'All day'
      : `${blockTime.startTime} – ${blockTime.endTime}`
  return { date, time }
}

const DESTROY_BLOCK_TIME = gql`
  mutation DestroyBlockTime($id: ID!) {
    destroyCoachAvailabilityBlockTime(id: $id) {
      success
      message
    }
  }
`

const BlockTimeDestroy = ({ blockTime, onCancel, onSuccess }) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()

  const [destroyBlockTime] = useMutation(DESTROY_BLOCK_TIME, {
    onCompleted: data => {
      if (data.destroyCoachAvailabilityBlockTime.success) {
        enqueueSnackbar('Blocked time deleted', { variant: 'success' })
        onSuccess && onSuccess()
      } else {
        enqueueSnackbar(
          data.destroyCoachAvailabilityBlockTime.message ||
            'Failed to delete blocked time',
          { variant: 'error' },
        )
      }
    },
  })

  const onSubmit = () => {
    destroyBlockTime({ variables: { id: blockTime.id } })
  }

  const { date, time } = formatBlockForDisplay(blockTime)
  const note = blockTime.blockNote

  return (
    <>
      <Box mb={2}>
        <Typography className={classes.bodyText}>
          Deleting this blocked time will allow students to schedule
          appointments with you during this time.
        </Typography>
      </Box>

      <Box className={classes.detailsCard} mb={2}>
        <BlockIcon className={classes.detailsIcon} fontSize="small" />
        <Box className={classes.detailsTextCol}>
          <Typography variant="body2" className={classes.detailsDate}>
            {date}
          </Typography>
          <Typography variant="body2" className={classes.detailsTime}>
            {[time, note].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      </Box>

      <Box mb={1}>
        <Typography className={classes.confirmText}>
          Are you sure you want to delete it?
        </Typography>
      </Box>

      <Box className={classes.actions}>
        <Button className={classes.cancelAction} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          className={classes.deleteAction}
          startIcon={<DeleteOutlinedIcon />}
          onClick={onSubmit}
        >
          Delete
        </Button>
      </Box>
    </>
  )
}

export default BlockTimeDestroy
